import React, { useState, useEffect } from 'react';
import { 
  Users, Crown, Search, Filter, Mail, Phone, MapPin, Calendar,
  Loader2, RefreshCw, ChevronDown, ChevronUp, Star, Heart,
  UserCheck, UserX, Award, TrendingUp, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

const TIER_COLORS = {
  free: 'bg-slate-100 text-slate-700',
  bronze: 'bg-amber-100 text-amber-700',
  silver: 'bg-slate-200 text-slate-700',
  gold: 'bg-yellow-100 text-yellow-700'
};

const TIER_ICONS = {
  free: Users,
  bronze: Award,
  silver: Star,
  gold: Crown
};

export function AdminMembershipTab() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterNewsletter, setFilterNewsletter] = useState('all');
  const [filterCommittee, setFilterCommittee] = useState('all');
  
  // Member detail dialog
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Tier update
  const [updatingTier, setUpdatingTier] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load stats
      const statsRes = await fetch(`${API_URL}/api/membership/admin/stats`, {
        headers: getAuthHeaders()
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Load members
      await loadMembers();
    } catch (error) {
      console.error('Error loading membership data:', error);
      toast.error('Failed to load membership data');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      let url = `${API_URL}/api/membership/admin/members?limit=100`;
      if (filterTier !== 'all') url += `&tier=${filterTier}`;
      if (filterNewsletter !== 'all') url += `&newsletter=${filterNewsletter === 'yes'}`;
      if (filterCommittee !== 'all') url += `&committee=${filterCommittee === 'yes'}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadMembers();
    }
  }, [filterTier, filterNewsletter, filterCommittee]);

  const handleSearch = () => {
    loadMembers();
  };

  const loadMemberDetail = async (memberId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/membership/admin/members/${memberId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setSelectedMember(await res.json());
      }
    } catch (error) {
      console.error('Error loading member detail:', error);
      toast.error('Failed to load member details');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateMemberTier = async (memberId, newTier) => {
    setUpdatingTier(true);
    try {
      const res = await fetch(`${API_URL}/api/membership/admin/members/${memberId}/tier`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ tier: newTier })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Member upgraded to ${newTier}${result.discount_code ? ` - Discount code: ${result.discount_code}` : ''}`);
        loadMembers();
        if (selectedMember) {
          loadMemberDetail(memberId);
        }
      } else {
        toast.error('Failed to update tier');
      }
    } catch (error) {
      toast.error('Failed to update tier');
    } finally {
      setUpdatingTier(false);
    }
  };

  const updateMemberStatus = async (memberId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/membership/admin/members/${memberId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Member status updated to ${newStatus}`);
        loadMembers();
        if (selectedMember) {
          loadMemberDetail(memberId);
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Membership Management</h2>
          <p className="text-sm text-slate-500">Manage members, tiers, and benefits</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Users className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total_members}</p>
                  <p className="text-xs text-slate-500">Total Members</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {Object.entries(stats.by_tier || {}).map(([tier, count]) => {
            const TierIcon = TIER_ICONS[tier] || Users;
            return (
              <Card key={tier}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${TIER_COLORS[tier]?.split(' ')[0] || 'bg-slate-100'}`}>
                      <TierIcon className={`w-5 h-5 ${tier === 'gold' ? 'text-yellow-600' : tier === 'silver' ? 'text-slate-600' : tier === 'bronze' ? 'text-amber-600' : 'text-slate-600'}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{count}</p>
                      <p className="text-xs text-slate-500 capitalize">{tier}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.newsletter_subscribers}</p>
                  <p className="text-xs text-slate-500">Newsletter</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {stats.pending_approval > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <UserCheck className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">{stats.pending_approval}</p>
                    <p className="text-xs text-amber-600">Pending Approval</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Search</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} size="sm">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="w-32">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Tier</label>
              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-36">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Newsletter</label>
              <Select value={filterNewsletter} onValueChange={setFilterNewsletter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Subscribed</SelectItem>
                  <SelectItem value="no">Not Subscribed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-36">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Committee</label>
              <Select value={filterCommittee} onValueChange={setFilterCommittee}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Committee Only</SelectItem>
                  <SelectItem value="no">Non-Committee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Members ({members.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No members found</p>
              <p className="text-sm">Members will appear here when they register</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Newsletter</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => {
                  const TierIcon = TIER_ICONS[member.tier] || Users;
                  return (
                    <TableRow 
                      key={member.member_id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => loadMemberDetail(member.member_id)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{member.first_name} {member.last_name}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                          {member.is_committee && (
                            <Badge variant="outline" className="text-xs mt-1 bg-violet-50 text-violet-700">
                              Committee
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={TIER_COLORS[member.tier]}>
                          <TierIcon className="w-3 h-3 mr-1" />
                          {member.tier}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {member.city || member.region || '-'}
                      </TableCell>
                      <TableCell>
                        {member.newsletter_opt_in ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">Yes</Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(member.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          member.status === 'active' ? 'secondary' : 
                          member.status === 'pending_approval' ? 'outline' : 
                          'destructive'
                        } className={member.status === 'pending_approval' ? 'bg-amber-100 text-amber-700 border-amber-300' : ''}>
                          {member.status === 'pending_approval' ? 'Pending Approval' : member.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Member Detail Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${TIER_COLORS[selectedMember.tier]?.split(' ')[0] || 'bg-slate-100'}`}>
                  {React.createElement(TIER_ICONS[selectedMember.tier] || Users, { className: 'w-6 h-6' })}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedMember.first_name} {selectedMember.last_name}</h3>
                  <p className="text-slate-500">{selectedMember.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Phone:</span>
                  <span className="ml-2">{selectedMember.phone || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Location:</span>
                  <span className="ml-2">{[selectedMember.city, selectedMember.region, selectedMember.country].filter(Boolean).join(', ') || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Experience:</span>
                  <span className="ml-2 capitalize">{selectedMember.dance_experience || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Joined:</span>
                  <span className="ml-2">{formatDate(selectedMember.created_at)}</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Preferences</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.newsletter_opt_in && <Badge className="bg-blue-100 text-blue-700">Newsletter</Badge>}
                  {selectedMember.volunteer_interest && <Badge className="bg-green-100 text-green-700">Volunteer</Badge>}
                  {selectedMember.working_group_interest && <Badge className="bg-purple-100 text-purple-700">Working Group</Badge>}
                  {selectedMember.is_committee && <Badge className="bg-violet-100 text-violet-700">Committee</Badge>}
                </div>
              </div>

              {selectedMember.discount_code && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Discount Code</h4>
                  <code className="bg-slate-100 px-3 py-1 rounded text-sm">{selectedMember.discount_code}</code>
                  <span className="ml-2 text-slate-500">({selectedMember.discount_percentage}% off)</span>
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Membership Tier</h4>
                <Select 
                  value={selectedMember.tier} 
                  onValueChange={(tier) => updateMemberTier(selectedMember.member_id, tier)}
                  disabled={updatingTier}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="bronze">Bronze (£2.99/mo)</SelectItem>
                    <SelectItem value="silver">Silver (£5.99/mo)</SelectItem>
                    <SelectItem value="gold">Gold (£9.99/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 flex gap-2 flex-wrap">
                {selectedMember.status === 'pending_approval' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => updateMemberStatus(selectedMember.member_id, 'active')}
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    Approve Membership
                  </Button>
                )}
                {selectedMember.status === 'active' ? (
                  <Button 
                    variant="outline" 
                    className="text-red-600"
                    onClick={() => updateMemberStatus(selectedMember.member_id, 'suspended')}
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Suspend Member
                  </Button>
                ) : selectedMember.status !== 'pending_approval' && (
                  <Button 
                    variant="outline"
                    onClick={() => updateMemberStatus(selectedMember.member_id, 'active')}
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    Reactivate Member
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminMembershipTab;
